import React from 'react';
import { RiCloseFill } from 'react-icons/ri';
import { MdOutlineClose } from 'react-icons/md';
import { useBannerDismiss } from '../../Hooks/useBannerDismiss';
import styles from './InfoCard.module.scss';

export type InfoCardVariant = 'dark' | 'light';

type ActionConfig = { label: string; onClick: () => void };

type Props = {
  id: string;
  variant?: InfoCardVariant;
  icon?: React.ReactNode;
  title: string;
  body?: string | React.ReactNode;
  action?: ActionConfig;
  footer?: React.ReactNode; // light variant only — custom button(s) in place of the text CTA
  chipId?: string;
  className?: string;
  style?: React.CSSProperties;
  onClose?: () => void; // controlled mode — bypasses sessionStorage, parent manages visibility
  onDismiss?: () => void; // uncontrolled mode only — fires alongside internal dismiss
};

const InfoCard = ({
  id,
  variant = 'dark',
  icon,
  title,
  body,
  action,
  footer,
  chipId,
  className = '',
  style,
  onClose,
  onDismiss,
}: Props) => {
  const { isDismissed, isExiting, dismiss } = useBannerDismiss(id, { chipId });

  const isControlled = onClose !== undefined;
  const handleClose = isControlled
    ? onClose
    : () => { dismiss(); onDismiss?.(); };

  if (!isControlled && isDismissed) return null;

  if (variant === 'light') {
    return (
      <div
        className={`${styles.card} ${styles.light} ${isExiting ? styles.exit : styles.enter} ${className}`}
        style={style}
      >
        <div className={styles.lightHeader}>
          <p className={styles.lightTitle}>{title}</p>
          <button className={styles.lightClose} onClick={handleClose} aria-label="Dismiss">
            <MdOutlineClose size={16} />
          </button>
        </div>

        {body && <div className={styles.lightBody}>{body}</div>}

        {footer && <div>{footer}</div>}

        {!footer && action && (
          <div className={styles.lightFooter}>
            <button className={styles.lightCta} onClick={action.onClick}>
              {action.label}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${styles.card} ${styles.dark} ${isExiting ? styles.exit : styles.enter} ${className}`}
      style={style}
    >
      <button className={styles.darkClose} onClick={handleClose} aria-label="Dismiss">
        <RiCloseFill size={17} />
      </button>

      <div className={styles.darkTitleRow}>
        {icon && <span className={styles.darkIcon}>{icon}</span>}
        <p className={styles.darkTitle}>{title}</p>
      </div>

      <div className={styles.darkBody}>{body}</div>

      {action && (
        <button className={styles.darkCta} onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
};

export default InfoCard;
